// @deprecated F12.1 — Legacy site-wide Footer (Italian 4-column:
//             Prodotto · Risorse · Azienda · Legale). Replaced by
//             FooterV4 (`components/footer-v4/FooterV4.jsx`, English
//             Product · Resources · Legal · Account). Only mounted when
//             `REACT_APP_HEADER_V4_ENABLED=false` for instant rollback.
//             Do not add new features here — add them in FooterV4.
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Twitter, Linkedin, Github, ChevronDown } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const SECTIONS = [
    {
        key: "product",
        links: [
            { key: "lead_engine", to: "/leads" },
            { key: "outreach", to: "/outreach" },
            { key: "marketplace", to: "/marketplace" },
            { key: "universe", to: "/universe" },
            { key: "modules", to: "/modules" },
            { key: "blog", to: "/blog" },
        ],
    },
    {
        key: "resources",
        links: [
            { key: "docs", to: "#" },
            { key: "status", to: "#" },
            { key: "community", to: "#" },
            { key: "support", to: "mailto:support@globalleadsuite.example" },
        ],
    },
    {
        key: "company",
        links: [
            { key: "about", to: "/#about" },
            { key: "pricing", to: "/app/subscription" },
            { key: "careers", to: "#" },
            { key: "affiliate", to: "/affiliate" },
        ],
    },
    {
        key: "legal",
        links: [
            { key: "privacy", to: "/legal/privacy" },
            { key: "terms", to: "/legal/terms" },
            { key: "cookies", to: "/legal/cookies" },
            { key: "gdpr", to: "/legal/gdpr" },
        ],
    },
];

const YEAR = new Date().getFullYear();

/**
 * Global footer. Sits on every public page (landing, pricing, blog,
 * legal, and any future public read views). Mobile: accordion.
 */
export default function Footer() {
    const { t } = useTranslation();
    return (
        <footer
            data-testid="footer"
            className="border-t border-[var(--color-border)] bg-[var(--color-panel-ai)] mt-24"
        >
            <div className="mx-auto max-w-6xl px-6 py-14">
                <div className="grid gap-8 md:grid-cols-4">
                    {SECTIONS.map((s) => (
                        <FooterSection
                            key={s.key}
                            sectionKey={s.key}
                            title={t(`footer.${s.key}`)}
                            links={s.links.map((l) => ({
                                to: l.to,
                                label: t(`footer.link.${l.key}`),
                                external: l.to.startsWith("http") || l.to.startsWith("mailto:"),
                            }))}
                        />
                    ))}
                </div>

                <div className="mt-14 pt-8 border-t border-[var(--color-border)] flex flex-col md:flex-row items-start md:items-center gap-4 justify-between">
                    <div className="flex items-center gap-3">
                        <span className="h-7 w-7 rounded-md bg-white hover:bg-[#EDEDED] grid place-items-center text-black font-semibold text-sm">
                            G
                        </span>
                        <span
                            data-testid="footer-copyright"
                            className="text-xs text-text-secondary"
                        >
                            © {YEAR} Global Lead Suite. {t("footer.rights_reserved")}
                        </span>
                    </div>

                    <div className="flex items-center gap-3">
                        <LanguageSwitcher compact />
                        <a
                            data-testid="footer-social-twitter"
                            href="https://twitter.com/"
                            target="_blank"
                            rel="noreferrer"
                            aria-label="Twitter"
                            className="text-text-secondary hover:text-text-main p-2 rounded-full hover:bg-[var(--color-card)] transition-colors"
                        >
                            <Twitter className="h-4 w-4" />
                        </a>
                        <a
                            data-testid="footer-social-linkedin"
                            href="https://linkedin.com/"
                            target="_blank"
                            rel="noreferrer"
                            aria-label="LinkedIn"
                            className="text-text-secondary hover:text-text-main p-2 rounded-full hover:bg-[var(--color-card)] transition-colors"
                        >
                            <Linkedin className="h-4 w-4" />
                        </a>
                        <a
                            data-testid="footer-social-github"
                            href="https://github.com/"
                            target="_blank"
                            rel="noreferrer"
                            aria-label="GitHub"
                            className="text-text-secondary hover:text-text-main p-2 rounded-full hover:bg-[var(--color-card)] transition-colors"
                        >
                            <Github className="h-4 w-4" />
                        </a>
                    </div>
                </div>
            </div>
        </footer>
    );
}

function FooterSection({ sectionKey, title, links }) {
    const [open, setOpen] = useState(false);
    return (
        <div data-testid={`footer-section-${sectionKey}`} className="md:block">
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className="w-full flex items-center justify-between text-left md:pointer-events-none md:mb-4"
            >
                <span className="text-[11px] uppercase tracking-widest text-text-secondary font-medium">
                    {title}
                </span>
                <ChevronDown
                    className={`h-3.5 w-3.5 text-text-secondary md:hidden transition-transform ${
                        open ? "rotate-180" : ""
                    }`}
                />
            </button>
            <ul
                className={`space-y-3 mt-3 md:mt-0 md:block ${open ? "block" : "hidden"}`}
            >
                {links.map((l) => (
                    <li key={l.to + l.label}>
                        {l.external ? (
                            <a
                                href={l.to}
                                data-testid={`footer-link-${sectionKey}-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                                className="text-sm text-text-secondary hover:text-text-main transition-colors"
                            >
                                {l.label}
                            </a>
                        ) : (
                            <Link
                                to={l.to}
                                data-testid={`footer-link-${sectionKey}-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                                className="text-sm text-text-secondary hover:text-text-main transition-colors"
                            >
                                {l.label}
                            </Link>
                        )}
                    </li>
                ))}
            </ul>
        </div>
    );
}
